/**
 * ClientWorkspaceHeader — compact toolbar header for the lead workspace.
 *
 * Absorbs the old bottom "Client Details", map, and marketing-attribution
 * cards: editable lead name, status badge, contact chips, a marketing
 * attribution badge (source, popover for UTM detail), and a pop-out
 * greyscale map of the client's location.
 */

import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import type { Lead, LeadStatus } from '../../types';
import LeadStatusPill from '../leads/LeadStatusPill';
import {
  ArrowLeft,
  Building2,
  DollarSign,
  FileText,
  HardHat,
  HelpCircle,
  Mail,
  MapPin,
  MousePointer,
  PenLine,
  Phone,
  Tag,
  Target,
} from 'lucide-react';
import './ClientWorkspaceHeader.css';

/** Greyscale map style — desaturate everything (no such style existed in the repo before). */
const GREYSCALE_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { stylers: [{ saturation: -100 }] },
];

interface ClientWorkspaceHeaderProps {
  lead: Lead;
  onStatusChange?: (status: LeadStatus) => void;
  editingName: boolean;
  editNameValue: string;
  onEditNameChange: (v: string) => void;
  onStartEditName: () => void;
  onSaveName: () => void;
  onCancelEditName: () => void;
  onEditClientInfo: () => void;
  mapsLoaded: boolean;
  onBack: () => void;
  onHelp: () => void;
  onOpenProjectHub?: () => void;
}

function formatSource(source: string): string {
  return source.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const ClientWorkspaceHeader: React.FC<ClientWorkspaceHeaderProps> = ({
  lead,
  onStatusChange,
  editingName,
  editNameValue,
  onEditNameChange,
  onStartEditName,
  onSaveName,
  onCancelEditName,
  onEditClientInfo,
  mapsLoaded,
  onBack,
  onHelp,
  onOpenProjectHub,
}) => {
  const [mapOpen, setMapOpen] = useState(false);
  const [attribOpen, setAttribOpen] = useState(false);
  const mapPopRef = useRef<HTMLDivElement | null>(null);
  const attribPopRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!mapOpen && !attribOpen) return;
    const onDown = (e: MouseEvent) => {
      if (mapOpen && mapPopRef.current && !mapPopRef.current.contains(e.target as Node)) {
        setMapOpen(false);
      }
      if (attribOpen && attribPopRef.current && !attribPopRef.current.contains(e.target as Node)) {
        setAttribOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [mapOpen, attribOpen]);

  const hasMap = mapsLoaded && !!lead.coordinates && !!lead.geocoded;
  const firstTouch = lead.trackingData?.firstTouchUtm;
  const lastTouch = lead.trackingData?.lastTouchUtm;
  const flatUtm = lead.trackingData;
  const hasUtmDetail = !!(
    firstTouch || lastTouch || flatUtm?.utmSource || lead.metaAdCampaign || lead.metaAdSet || lead.metaAdName
  );

  return (
    <header className="cw-header" data-tour="cw-header">
      <button type="button" onClick={onBack} className="cw-header-back" aria-label="Go back">
        <ArrowLeft size={18} />
      </button>

      <div className="cw-header-identity">
        {editingName ? (
          <input
            type="text"
            className="cw-header-name-input"
            value={editNameValue}
            onChange={e => onEditNameChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') onSaveName();
              if (e.key === 'Escape') onCancelEditName();
            }}
            onBlur={onSaveName}
            placeholder="Client name"
            autoFocus
          />
        ) : (
          <h1 className="cw-header-name" onClick={onStartEditName} title="Click to edit name">
            {lead.firstName} {lead.lastName}
          </h1>
        )}
        <LeadStatusPill
          status={lead.status}
          variant="header"
          onChange={onStatusChange}
          readonly={!onStatusChange}
        />
        <button
          type="button"
          className="cw-header-edit-info"
          onClick={onEditClientInfo}
          title="Edit client details"
          aria-label="Edit client details"
        >
          <PenLine size={13} />
        </button>
      </div>

      <div className="cw-header-chips">
        {lead.email && (
          <a href={`mailto:${lead.email}`} className="cw-chip" title={lead.email}>
            <Mail size={12} />
            <span>{lead.email}</span>
          </a>
        )}
        {lead.phone && (
          <a href={`tel:${lead.phone}`} className="cw-chip" title={lead.phone}>
            <Phone size={12} />
            <span>{lead.phone}</span>
          </a>
        )}
        {lead.company && (
          <span className="cw-chip subtle" title={lead.company}>
            <Building2 size={12} />
            <span>{lead.company}</span>
          </span>
        )}
        {lead.projectType && (
          <span className="cw-chip subtle" title={lead.projectType}>
            <FileText size={12} />
            <span>{lead.projectType}</span>
          </span>
        )}
        {lead.budget > 0 && (
          <span className="cw-chip subtle" title={`Budget $${lead.budget.toLocaleString()}`}>
            <DollarSign size={12} />
            <span>${lead.budget.toLocaleString()}</span>
          </span>
        )}

        {/* Location → greyscale map pop-out */}
        <div className="cw-header-pop-anchor" ref={mapPopRef}>
          <button
            type="button"
            className={`cw-chip ${hasMap ? 'clickable' : 'subtle'} ${mapOpen ? 'open' : ''}`}
            onClick={() => hasMap && setMapOpen(o => !o)}
            disabled={!hasMap && !lead.location}
            title={hasMap ? 'Show location map' : lead.location || 'No location'}
            data-tour="cw-map-chip"
          >
            <MapPin size={12} />
            <span>{lead.location || 'No location'}</span>
          </button>
          {mapOpen && hasMap && lead.coordinates && (
            <div className="cw-map-popover" role="dialog" aria-label="Client location map">
              <GoogleMap
                mapContainerClassName="cw-map-popover-canvas"
                center={{ lat: lead.coordinates.lat, lng: lead.coordinates.lng }}
                zoom={12}
                options={{
                  disableDefaultUI: true,
                  zoomControl: true,
                  clickableIcons: false,
                  styles: GREYSCALE_MAP_STYLES,
                }}
              >
                <Marker position={{ lat: lead.coordinates.lat, lng: lead.coordinates.lng }} />
              </GoogleMap>
              <div className="cw-map-popover-label">
                <MapPin size={11} />
                <span>{lead.location}</span>
              </div>
            </div>
          )}
        </div>

        {/* Marketing attribution badge */}
        <div className="cw-header-pop-anchor" ref={attribPopRef}>
          <button
            type="button"
            className={`cw-attrib-badge ${attribOpen ? 'open' : ''}`}
            onClick={() => hasUtmDetail && setAttribOpen(o => !o)}
            title={hasUtmDetail ? 'Marketing attribution — click for UTM detail' : 'Lead source'}
            data-tour="cw-attribution"
            data-source={lead.source}
          >
            <Target size={12} />
            <span>{formatSource(lead.source)}</span>
          </button>
          {attribOpen && hasUtmDetail && (
            <div className="cw-attrib-popover" role="dialog" aria-label="Marketing attribution detail">
              {firstTouch && (
                <div className="cw-attrib-group">
                  <span className="cw-attrib-group-label"><MousePointer size={11} /> First touch</span>
                  {firstTouch.utmSource && <div className="cw-attrib-row"><span>Source</span><strong>{firstTouch.utmSource}</strong></div>}
                  {firstTouch.utmMedium && <div className="cw-attrib-row"><span>Medium</span><strong>{firstTouch.utmMedium}</strong></div>}
                  {firstTouch.utmCampaign && <div className="cw-attrib-row"><span>Campaign</span><strong>{firstTouch.utmCampaign}</strong></div>}
                </div>
              )}
              {lastTouch && (
                <div className="cw-attrib-group">
                  <span className="cw-attrib-group-label"><MousePointer size={11} /> Last touch</span>
                  {lastTouch.utmSource && <div className="cw-attrib-row"><span>Source</span><strong>{lastTouch.utmSource}</strong></div>}
                  {lastTouch.utmMedium && <div className="cw-attrib-row"><span>Medium</span><strong>{lastTouch.utmMedium}</strong></div>}
                  {lastTouch.utmCampaign && <div className="cw-attrib-row"><span>Campaign</span><strong>{lastTouch.utmCampaign}</strong></div>}
                </div>
              )}
              {!firstTouch && !lastTouch && flatUtm?.utmSource && (
                <div className="cw-attrib-group">
                  <span className="cw-attrib-group-label"><MousePointer size={11} /> UTM</span>
                  <div className="cw-attrib-row"><span>Source</span><strong>{flatUtm.utmSource}</strong></div>
                  {flatUtm.utmMedium && <div className="cw-attrib-row"><span>Medium</span><strong>{flatUtm.utmMedium}</strong></div>}
                  {flatUtm.utmCampaign && <div className="cw-attrib-row"><span>Campaign</span><strong>{flatUtm.utmCampaign}</strong></div>}
                </div>
              )}
              {(lead.metaAdCampaign || lead.metaAdSet || lead.metaAdName) && (
                <div className="cw-attrib-group">
                  <span className="cw-attrib-group-label"><Tag size={11} /> Meta Ads</span>
                  {lead.metaAdCampaign && <div className="cw-attrib-row"><span>Campaign</span><strong>{lead.metaAdCampaign}</strong></div>}
                  {lead.metaAdSet && <div className="cw-attrib-row"><span>Ad set</span><strong>{lead.metaAdSet}</strong></div>}
                  {lead.metaAdName && <div className="cw-attrib-row"><span>Ad</span><strong>{lead.metaAdName}</strong></div>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="cw-header-actions">
        <button
          type="button"
          className="cw-header-btn"
          onClick={onHelp}
          data-tour="cw-help-btn"
          aria-label="Help"
        >
          <HelpCircle size={15} />
          <span>Help</span>
        </button>
        {onOpenProjectHub && (
          <button
            type="button"
            className="cw-header-btn"
            onClick={onOpenProjectHub}
            title="Open Project Hub for this deal"
          >
            <HardHat size={15} />
            <span>Project Hub</span>
          </button>
        )}
      </div>
    </header>
  );
};

export default ClientWorkspaceHeader;
